import { Assignment, Route } from '@mui/icons-material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined';
import LocalTaxiOutlinedIcon from '@mui/icons-material/LocalTaxiOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { VATTotalPrice } from 'api/common-types';
import { CallerType } from 'api/models/Common/JyrneyEnums';
import { suppliersAPI } from 'api/supplier/suppliersAPI';
import { supplierGroupsAPI } from 'api/supplierGroup/supplierGroupAPI';
import isSelectedService from 'components/Bookings/Common/isSelectedService';
import AddressDisplay from 'components/Common/AddressDisplay/AddressDisplay';
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice';
import milesDisplay from 'components/Common/DisplayValues/milesDisplay';
import VehicleDisplayName from 'components/Common/VehicleDisplayName/VehicleDisplayName';
import { AnimatedChip } from 'components/FormFields/PolicyFields/PolicyFields';
import FallbackButton from 'components/Voice/FallbackButton';
import useClient from 'hooks/useClient';
import useUserRole from 'hooks/useUserRole';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookingDetailsProps } from '../BookingStatusPage';
import { panelActionButtonSx } from './panelActionStyles';

type EntryWrapperProps = {
  children: React.ReactNode
}

type DriverVehicle = {
  makeAndModel: string
  registrationNumber?: string
  colour?: string
}

type VehicleDetailsProps = {
  vehicleDetails: string
  estimatedPrice: VATTotalPrice
  fixedPrice: boolean
  estimatedDistance: number
  meetAndGreet?: boolean
  displayVATPrice: boolean
  driverVehicle?: DriverVehicle
}

type RouteDetailsProps = {
  pickup: any
  additionalStops: any[]
  dropOff: any
}

const routeMarkerStyles = {
  origin: {
    background: '#0b7a32',
    border: '#085924',
    color: '#ffffff',
    label: 'A',
  },
  destination: {
    background: '#3b51f6',
    border: '#0943a1',
    color: '#ffffff',
    label: 'B',
  },
  stop: {
    background: '#f0c419',
    border: '#d5a900',
    color: '#12212b',
    label: 'S',
  },
}
type FlightDetailsProps = {
  flightNumber: string
  meetAndGreet?: boolean
}

type DriveDetailsProps = {
  driverName: string
  driverContactNumber: string
  jobId: string
  supplierName: string
  supplierId: string
  supplierNumber: string
}

type VehicleMetricProps = {
  icon: React.ReactNode
  label: React.ReactNode
  value: React.ReactNode
  labelAccessory?: React.ReactNode
  secondary?: React.ReactNode
}

type AdaptiveMetricLabelProps = {
  fullLabel: string
  shortLabel: string
}

const estimatedDistanceToMileage = (estimatedDistance?: number | null) => {
  if (
    typeof estimatedDistance !== 'number' ||
    !Number.isFinite(estimatedDistance)
  ) {
    return 'N/A'
  }

  const roundedDistance = Math.round(estimatedDistance * 10) / 10
  return milesDisplay({ miles: roundedDistance })
}

const EntryWrapper = ({ children }: EntryWrapperProps) => (
  <Box
    component="div"
    sx={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
    }}
  >
    {children}
  </Box>
)

const AdaptiveMetricLabel = ({
  fullLabel,
  shortLabel,
}: AdaptiveMetricLabelProps) => {
  const containerRef = React.useRef<HTMLSpanElement | null>(null)
  const measureRef = React.useRef<HTMLSpanElement | null>(null)
  const [useShortLabel, setUseShortLabel] = React.useState(false)

  React.useEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const updateLabel = () => {
      setUseShortLabel(measure.offsetWidth > container.clientWidth)
    }

    updateLabel()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateLabel)
      return () => window.removeEventListener('resize', updateLabel)
    }

    const resizeObserver = new ResizeObserver(updateLabel)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [fullLabel])

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'block',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Box
        component="span"
        ref={containerRef}
        sx={{
          display: 'block',
          width: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {useShortLabel ? shortLabel : fullLabel}
      </Box>
      <Box
        component="span"
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          height: 0,
          overflow: 'hidden',
        }}
      >
        {fullLabel}
      </Box>
    </Box>
  )
}

const VehicleMetric = ({
  icon,
  label,
  value,
  labelAccessory,
  secondary,
}: VehicleMetricProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: 0,
      padding: '5px 8px',
      borderRadius: '12px',
      backgroundColor: '#f4f4f4',
      border: '1px solid #e5e5e5',
      flex: '1 1 0',
    }}
  >
    <Box
      sx={{
        color: '#8a8a8a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          minWidth: 0,
          width: '100%',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#8a8a8a',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            lineHeight: 1.2,
            flex: '1 1 auto',
            fontSize: '0.75rem',
            minWidth: 0,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: '#3b3b3b',
          fontWeight: 500,
          lineHeight: 1.3,
          overflowWrap: 'anywhere',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          fontSize: '0.75rem',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>

      {secondary ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {secondary}
        </Box>
      ) : null}
    </Box>
    {labelAccessory ? (
      <Box sx={{ display: 'inline-flex', flexShrink: 0, marginLeft: 'auto' }}>
        {labelAccessory}
      </Box>
    ) : null}
  </Box>
)

const VehicleDetails = ({
  vehicleDetails,
  driverVehicle,
  estimatedPrice,
  estimatedDistance,
  fixedPrice,
  meetAndGreet,
  displayVATPrice,
}: VehicleDetailsProps) => {
  const priceValue = DisplayPrice({
    VATTotalPrice: estimatedPrice,
    priceType: fixedPrice ? 'exact' : 'range',
    displayVATPrice: displayVATPrice,
  })
  const hasDriverVehicle = Boolean(
    driverVehicle && Object.keys(driverVehicle).length > 0,
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '5px',
        margin: 0,
        width: '100%',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          margin: 0,
          width: '100%',
          alignItems: 'stretch',
        }}
      >
        <VehicleMetric
          icon={<LocalTaxiOutlinedIcon />}
          label="Group"
          value={vehicleDetails}
          labelAccessory={
            meetAndGreet ? (
              <AnimatedChip
                size="small"
                icon={<Assignment />}
                label="Meet & Greet"
                sx={(theme: any) => ({
                  height: '20px',
                  borderRadius: '999px',
                  backgroundColor: theme.palette.info.light,
                  color: theme.palette.info.dark,
                  '& .MuiChip-label': {
                    paddingX: '7px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  },
                  '& .MuiChip-icon': {
                    marginLeft: '7px',
                    marginRight: '-3px',
                    color: 'inherit',
                    fontSize: '0.75rem',
                  },
                })}
              />
            ) : undefined
          }
        />
        <VehicleMetric
          icon={<Route />}
          label={
            <AdaptiveMetricLabel
              fullLabel="Estimated Distance"
              shortLabel="Distance"
            />
          }
          value={`${estimatedDistanceToMileage(estimatedDistance)} (est.)`}
        />
        <VehicleMetric
          icon={<PaymentsOutlinedIcon />}
          label={
            fixedPrice ? (
              'Fixed Price'
            ) : (
              <AdaptiveMetricLabel
                fullLabel="Estimated Price"
                shortLabel="Est. Price"
              />
            )
          }
          value={priceValue}
        />
      </Box>
      {hasDriverVehicle && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          <VehicleMetric
            icon={<LocalTaxiOutlinedIcon />}
            label="Vehicle"
            value={`${driverVehicle?.colour ? driverVehicle.colour : ''} ${
              driverVehicle?.makeAndModel ? driverVehicle.makeAndModel : ''
            }`}
          />

          {driverVehicle?.registrationNumber && (
            <VehicleMetric
              icon={<LocalTaxiOutlinedIcon />}
              label="Reg. Number"
              value={`${driverVehicle.registrationNumber}`}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

const RouteMarker = ({
  kind,
  label,
}: {
  kind: keyof typeof routeMarkerStyles
  label?: string | number
}) => {
  const style = routeMarkerStyles[kind]

  return (
    <Box
      component="span"
      sx={{
        width: '30px',
        height: '30px',
        borderRadius: '999px',
        backgroundColor: style.background,
        border: `2px solid ${style.border}`,
        color: style.color,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 700,
        boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
        flexShrink: 0,
      }}
    >
      {label ?? style.label}
    </Box>
  )
}

const RouteDetails = ({
  pickup,
  dropOff,
  additionalStops = [],
}: RouteDetailsProps) => {
  const routeStops = Array.isArray(additionalStops) ? additionalStops : []
  const [showStops, setShowStops] = React.useState(false)

  return (
    <Box
      component="div"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
      }}
    >
      <EntryWrapper>
        <RouteMarker kind="origin" />
        <Box component="p" sx={{ padding: 0, margin: 0, flex: 1 }}>
          {AddressDisplay({ address: pickup })}
          {!dropOff?.addressLine1 && ' - Hourly Trip'}
        </Box>
      </EntryWrapper>

      {showStops &&
        routeStops.map((stop, index) => (
          <EntryWrapper key={`stop-${index}`}>
            <RouteMarker kind="stop" label={index + 1} />
            <Box component="p" sx={{ padding: 0, margin: 0, flex: 1 }}>
              {AddressDisplay({ address: stop })}
            </Box>
          </EntryWrapper>
        ))}
      {routeStops.length > 0 && (
        <Box
          component="div"
          sx={{ display: 'flex', justifyContent: 'flex-start', p: '0', m: 0 }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setShowStops((current) => !current)}
            sx={{
              border: 0,
              background: 'transparent',
              padding: 0,
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: 'inherit',
              color: 'var(--jy-navy-900, #00253d)',
              cursor: 'pointer',
            }}
          >
            <Chip
              sx={{ m: 0 }}
              size="small"
              label={`${showStops ? 'hide' : '+'} ${
                !showStops ? routeStops.length : ''
              } stop${routeStops.length > 1 || showStops ? 's' : ''}`}
            />
          </Box>
        </Box>
      )}
      {dropOff?.addressLine1 && (
        <EntryWrapper>
          <RouteMarker kind="destination" />
          <Box component="p" sx={{ padding: 0, margin: 0, flex: 1 }}>
            {AddressDisplay({ address: dropOff })}
          </Box>
        </EntryWrapper>
      )}
    </Box>
  )
}

const FlightDetails = ({ flightNumber }: FlightDetailsProps) => (
  <EntryWrapper>
    <EntryWrapper>
      <FlightOutlinedIcon />
      <Box component="p" sx={{ padding: 0, margin: 0 }}>
        {`${flightNumber}`}
      </Box>
    </EntryWrapper>
  </EntryWrapper>
)

const SupplierGroupBadge = ({ supplierId }: { supplierId: string }) => {
  const { hasRole } = useUserRole()
  const isJyrneyAdmin = hasRole(['Jyrney_Admin'])

  const supplierCall = useQuery({
    queryKey: ['getSupplierById', supplierId],
    queryFn: async () => suppliersAPI.getSupplierById(supplierId),
    enabled: isJyrneyAdmin && !!supplierId,
    refetchOnWindowFocus: false,
  })

  const supplierGroupId = supplierCall.data?.data?.supplierGroupId

  const supplierGroupCall = useQuery({
    queryKey: ['getSupplierGroupById', supplierGroupId],
    queryFn: async () =>
      supplierGroupsAPI.getSupplierGroupById(supplierGroupId!),
    enabled: isJyrneyAdmin && !!supplierGroupId,
    refetchOnWindowFocus: false,
  })

  if (!isJyrneyAdmin || !supplierGroupId) return null

  const groupName = supplierGroupCall.data?.data?.name ?? 'Loading…'

  return (
    <Tooltip title={groupName} placement="top">
      <ElectricalServicesOutlinedIcon fontSize="small" sx={{ opacity: 0.7 }} />
    </Tooltip>
  )
}

const DriverDetails = ({
  driverName,
  driverContactNumber,
  jobId,
  supplierName,
  supplierId,
  supplierNumber,
}: DriveDetailsProps) => (
  <EntryWrapper>
    <EntryWrapper>
      <BadgeOutlinedIcon />
      <EntryWrapper>
        <Box component="p" sx={{ padding: 0, margin: 0, fontWeight: 'bold' }}>
          Driver:
        </Box>
        <Box component="p" sx={{ padding: 0, margin: 0 }}>
          {driverName ? driverName : 'Not assigned'}
        </Box>
        {driverContactNumber && jobId && (
          <FallbackButton
            label={driverName}
            number={driverContactNumber}
            id={jobId}
            type={CallerType.DRIVER}
            sx={panelActionButtonSx}
          />
        )}
      </EntryWrapper>
    </EntryWrapper>
    <EntryWrapper>
      <LocalTaxiOutlinedIcon />
      <EntryWrapper>
        <Box component="p" sx={{ padding: 0, margin: 0, fontWeight: 'bold' }}>
          Operator:
        </Box>
        <Box component="p" sx={{ padding: 0, margin: 0 }}>
          {supplierName}
        </Box>
        {supplierId && <SupplierGroupBadge supplierId={supplierId} />}
        <FallbackButton
          label={supplierName}
          number={supplierNumber}
          id={supplierId}
          type={CallerType.SUPPLIER}
          sx={panelActionButtonSx}
        />
      </EntryWrapper>
    </EntryWrapper>
  </EntryWrapper>
)

const JourneyDetails = ({ selectedJob }: BookingDetailsProps) => {
  const { fixedPriceDisplay, displayVATPrice } = useClient(
    selectedJob?.client.clientId,
  )
  console.log('selectedJob', selectedJob)
  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '25px',
        padding: '10px 25px',
      }}
    >
      <VehicleDetails
        vehicleDetails={`${
          VehicleDisplayName({ vehicleDetails: selectedJob?.vehicleDetails }) ||
          ''
        }`}
        driverVehicle={selectedJob?.driver?.vehicle}
        estimatedPrice={selectedJob?.estimatedPrice}
        estimatedDistance={selectedJob?.estimatedJourneyDistance}
        fixedPrice={fixedPriceDisplay}
        displayVATPrice={displayVATPrice}
        meetAndGreet={
          selectedJob?.services
            ? isSelectedService({
                selectedService: 'MeetAndGreet',
                services: selectedJob?.services,
              })
            : false
        }
      />
      <RouteDetails
        pickup={selectedJob?.pickup}
        dropOff={selectedJob?.dropOff}
        additionalStops={selectedJob?.additionalStops}
      />
      <FlightDetails
        flightNumber={selectedJob?.flightDetails?.flightNumber || 'N/A'}
        meetAndGreet={true}
      />
      <DriverDetails
        driverName={selectedJob?.driver?.name || ''}
        driverContactNumber={selectedJob?.driver?.contactNumber}
        jobId={selectedJob?.id}
        supplierName={selectedJob?.supplier?.companyName || ''}
        supplierId={selectedJob?.supplier?.supplierId || ''}
        supplierNumber={selectedJob?.supplier?.contactNumber || ''}
      />
    </Box>
  )
}

export default JourneyDetails
